import React, { PropsWithChildren } from "react";
import { spacing } from "../../stitches.config";
import {
  StyledGrid,
  StyledSection,
  StyledSections,
} from "../Common/__styles__/Layout";

type ColumnsInput = number | { base?: number; M?: number; L?: number };

type SectionsProps = {
  gap?: (typeof spacing)[keyof typeof spacing];
  as?: keyof JSX.IntrinsicElements | React.ComponentType;
  onSubmit?: () => void;
};

export const Sections = ({
  children,
  gap = spacing.xl,
  as = "div",
  onSubmit,
}: PropsWithChildren<SectionsProps>) => {
  return (
    <StyledSections
      style={{ display: "flex", flexDirection: "column", gap: gap.value }}
      as={as}
      onSubmit={onSubmit}
      noValidate={as === "form"}
    >
      {children}
    </StyledSections>
  );
};

type SectionProps = {
  gap?: (typeof spacing)[keyof typeof spacing];
  flexDirection?: "column" | "row";
  style?: React.CSSProperties;
};

export const Section = ({
  children,
  gap = spacing.m,
  flexDirection = "column",
  style,
}: PropsWithChildren<SectionProps>) => {
  return (
    <StyledSection
      style={{ display: "flex", flexDirection, gap: gap.value, ...style }}
    >
      {children}
    </StyledSection>
  );
};

export type GridProps = {
  columns?: ColumnsInput;
  gap?: (typeof spacing)[keyof typeof spacing];
};

export const Grid = ({
  children,
  columns = 1,
  gap = spacing.m,
}: React.PropsWithChildren<GridProps>) => {
  if (typeof columns === "object" && (columns.M || columns.L)) {
    const baseCols = columns.base ?? 1;
    const mdCols = columns.M ?? baseCols;
    const lgCols = columns.L ?? mdCols;

    return (
      <StyledGrid
        css={{
          gridTemplateColumns: `repeat(${baseCols}, 1fr)`,
          "@md": {
            gridTemplateColumns: `repeat(${mdCols}, 1fr)`,
          },
          "@lg": {
            gridTemplateColumns: `repeat(${lgCols}, 1fr)`,
          },
          gap: gap.value,
        }}
      >
        {children}
      </StyledGrid>
    );
  }

  const colCount = typeof columns === "number" ? columns : (columns.base ?? 1);

  return (
    <StyledGrid
      css={{
        gridTemplateColumns: `repeat(${colCount}, 1fr)`,
        gap: gap.value,
      }}
    >
      {children}
    </StyledGrid>
  );
};
